import React, { useState, useEffect, useContext } from 'react'
import * as StringFormatter from '../../../../global/formatters/string-formatter.js'
import { getRelativeBasePath } from '../../../../global/environments/path.js'
import { getSeparatelyManagedAccountsData } from '../InvestmentProductsDataSource.js'
import ErrorBoundary from '../../.././../components/FundDetails/Modules/Performance/ErrorBoundary.jsx'
import FilterHeader from '../../../../components/PageHeader/FilterHeader.jsx'
import Table from '../../../../components/Table/Table.jsx'
import ManageAccountsFilters from './ManageAccountsFilters.jsx'
import {
  convertDate,
  stickyOnScroll,
  bodyScrollBehavior,
} from '../helpers/InvestmentProductsHelper.js'
import { MobileFundCard } from '../MobileFundCard.jsx'
import { ProductContext } from '../ProductContext.jsx'
import { SEPARATELY_MANAGED_ACCOUNTS_PATH } from '../../../../components/FundDetails/Helpers/FundTypes.js'
import { mapFactsheetUrls } from '../helpers/InvestmentProductsHelper.js'
import { DownloadFactsheet } from '../DownloadFactsheet.jsx'
import { removeSpecialCharacters } from '../../../utility/index.js'

import '../InvestmentProducts.css'

export default function ManageAccountsContainer() {
  const [funds, setFunds] = useState([])
  const [selectedFundSearch, setFundSearch] = useState('')
  const [grossAsOfDate, setGrossAsOfDate] = useState('')
  const [categoryOptions, setCategoryOptions] = useState({})
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState([])
  const [tableHeaderTop, setTableHeaderTop] = useState()
  const [cusipFactsheetMap, setCusipFactsheetMap] = useState({})
  const { isMobile } = useContext(ProductContext)

  const getFixedDecimal = (val) => {
    return isNaN(Number(val)) ? '--' : Number(val).toFixed(2)
  }

  const getAspect = (section) => {
    const aspects = [
      {
        name: '#separatelyManagedAccounts',
        info: {
          selector: [
            {
              name: 'value',
              isArray: false,
            },
            {
              name: 'managedAccount',
              isArray: false,
            },
            {
              name: 'compositeReturns',
              isArray: false,
            },
          ],
          getData: getSeparatelyManagedAccountsData,
          columnSummaries: [
            {
              name: 'headerSummary0',
              colSpan: 3,
              content: () => '',
            },
            {
              name: 'headerSummary1',
              colSpan: 6,
              content: () => {
                return (
                  <>
                    Average Annual Returns (%) as of{' '}
                    {convertDate(grossAsOfDate)}
                  </>
                )
              },
            },
            {
              name: 'headerSummary2',
              colSpan: 1,
              content: () => '',
            },
          ],
          columns: [
            {
              name: 'returnName',
              className: 'Table-header u-bgColorBrandDark',
              ariaLabel: 'Name',
              content: <div>SMA</div>,
              cell: {
                className:
                  'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0',
                content: (fund) => (
                  <a
                    className="u-textBold u-flex u-flexAlignItemsBaseline"
                    style={{ minWidth: 150 }}
                    href={
                      getRelativeBasePath() +
                      `/investment-products/managed-accounts/${StringFormatter.urlSafe(
                        removeSpecialCharacters(fund.fundName)
                      )}/Class-None/details/?cusip=${fund.accountID}`
                    }
                  >
                    {fund.fundName}
                  </a>
                ),
              },
              button: {
                className: 'Table-headerNode u-padding2gu u-paddingRight3gu',
                sort: 'portfolioName' || 'fundName',
                sortType: 'string',
              },
            },
            {
              name: 'inceptionDate',
              className: 'Table-header u-bgColorBrandDark',
              ariaLabel: 'Inception Date',
              content: <div>Inception Date</div>,
              cell: {
                className:
                  'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-textNoWrap',
                content: (fund) => convertDate(fund.inceptionDt),
              },
              button: {
                className: 'Table-headerNode u-padding2gu u-paddingRight3gu',
                sort: 'inceptionDt',
                sortType: 'date',
              },
            },
            {
              name: 'type',
              className: 'Table-header u-bgColorBrandDark bordered',
              ariaLabel: 'Type',
              cell: {
                className: 'u-borderColorNeutralLight u-borderTop u-textNoWrap',
                content: () => (
                  <>
                    <div className="u-padding2gu u-borderColorNeutralLight">
                      (pure gross)
                    </div>
                    <div className="u-padding2gu">(net)</div>
                  </>
                ),
              },
            },
            {
              name: 'ytd',
              className:
                'Table-header u-bgColorBrandDark u-textCenter u-textNoWrap',
              ariaLabel: 'YTD (cum.)',
              content: <div>YTD (cum.)</div>,
              cell: {
                className:
                  'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0 u-textNoWrap u-textCenter',
                padding: 'u-paddingHoriz0',
                content: (fund) => (
                  <>
                    <div className="u-padding2gu u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                      {getFixedDecimal(fund.ytdPercentGross)}
                    </div>
                    <div className="u-padding2gu">
                      {getFixedDecimal(fund.ytdPercentNet)}
                    </div>
                  </>
                ),
              },
            },
            {
              name: '1year',
              className:
                'Table-header u-bgColorBrandDark u-textCenter u-textNoWrap',
              ariaLabel: '1 Year',
              content: <div>1 Year</div>,
              cell: {
                className:
                  'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0 u-textNoWrap u-textCenter',
                padding: 'u-paddingHoriz0',
                content: (fund) => (
                  <>
                    <div className="u-padding2gu u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                      {getFixedDecimal(fund.grossOneYear)}
                    </div>
                    <div className="u-padding2gu">
                      {getFixedDecimal(fund.netOneYear)}
                    </div>
                  </>
                ),
              },
            },
            {
              name: '3year',
              className:
                'Table-header u-bgColorBrandDark u-textCenter u-textNoWrap',
              ariaLabel: '3 Year',
              content: <div>3 Year</div>,
              cell: {
                className:
                  'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0 u-textNoWrap u-textCenter',
                padding: 'u-paddingHoriz0',
                content: (fund) => (
                  <>
                    <div className="u-padding2gu u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                      {getFixedDecimal(fund.grossThreeYear)}
                    </div>
                    <div className="u-padding2gu">
                      {getFixedDecimal(fund.netThreeYear)}
                    </div>
                  </>
                ),
              },
            },
            {
              name: '5year',
              className:
                'Table-header u-bgColorBrandDark u-textCenter u-textNoWrap',
              ariaLabel: '5 Year',
              content: <div>5 Year</div>,
              cell: {
                className:
                  'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0 u-textNoWrap u-textCenter',
                padding: 'u-paddingHoriz0',
                content: (fund) => (
                  <>
                    <div className="u-padding2gu u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                      {getFixedDecimal(fund.grossFiveYear)}
                    </div>
                    <div className="u-padding2gu">
                      {getFixedDecimal(fund.netFiveYear)}
                    </div>
                  </>
                ),
              },
            },
            {
              name: '10year',
              className:
                'Table-header u-bgColorBrandDark u-textCenter u-textNoWrap',
              ariaLabel: '10 Year',
              content: <div>10 Year</div>,
              cell: {
                className:
                  'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0 u-textNoWrap u-textCenter',
                padding: 'u-paddingHoriz0',
                content: (fund) => (
                  <>
                    <div className="u-padding2gu u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                      {getFixedDecimal(fund.grossTenYear)}
                    </div>
                    <div className="u-padding2gu">
                      {getFixedDecimal(fund.netTenYear)}
                    </div>
                  </>
                ),
              },
            },
            {
              name: 'since',
              className:
                'Table-header u-bgColorBrandDark u-textCenter u-textNoWrap bordered',
              ariaLabel: 'Since Inception',
              content: <div>Since Inception</div>,
              cell: {
                className:
                  'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0 u-textNoWrap u-textCenter',
                padding: 'u-paddingHoriz0',
                content: (fund) => (
                  <>
                    <div className="u-padding2gu u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                      {getFixedDecimal(fund.grossSinceInception)}
                    </div>
                    <div className="u-padding2gu">
                      {getFixedDecimal(fund.netSinceInception)}
                    </div>
                  </>
                ),
              },
            },
            {
              name: 'downloadFactsheet',
              className: 'Table-header u-textRight',
              ariaLabel: 'Download Factsheet',
              content: (
                <>
                  Download
                  <br />
                  Factsheet
                </>
              ),
              cell: {
                className:
                  'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-textNoWrap',
                content: (fund) =>
                  cusipFactsheetMap[fund.accountID] ? (
                    <DownloadFactsheet
                      fund={fund}
                      factsheetData={cusipFactsheetMap[fund.accountID]}
                    />
                  ) : null,
              },
            },
          ],
        },
      },
    ]

    return aspects.find((a) => a.name === section).info
  }

  useEffect(() => {
    initializeFunds()
    getCusipFactsheetMap()
    stickyOnScroll(setTableHeaderTop)
  }, [])

  useEffect(() => {
    document.body.style.overflow = bodyScrollBehavior(isMobile, isFilterOpen)
  }, [isMobile, isFilterOpen])

  useEffect(() => {
    initializeFundsFiltered()
  }, [selectedFundSearch, selectedCategory])

  useEffect(() => {
    document
      .querySelectorAll('.Article-container')
      .forEach((c) => c.classList.remove('u-hidden'))
  }, [])

  const getCusipFactsheetMap = async () => {
    const data = await mapFactsheetUrls(SEPARATELY_MANAGED_ACCOUNTS_PATH)
    setCusipFactsheetMap(data)
  }

  const initializeFunds = async () => {
    const aspect = getAspect('#separatelyManagedAccounts')
    const data = await aspect.getData()
    const _categoryOptions = data?.value?.managedAccount?.compositeReturns
      ?.slice()
      .sort((a, b) =>
        (a?.categoryName ?? '').localeCompare(b?.categoryName ?? '')
      )
      .reduce((p, n) => ({ ...p, [n?.categoryName]: n?.categoryName }), {})
    setCategoryOptions(_categoryOptions)
    const _funds = aspect.selector.reduce(
      (prev, curr) => (curr.isArray ? prev[curr.name][0] : prev[curr.name]),
      data
    )
    setGrossAsOfDate(data?.value?.managedAccount?.grossAsOfDate)
    setFunds(_funds)
  }

  const onClickToggleFilter = (type) => {
    if (type) {
      setIsFilterOpen(!isFilterOpen)
    } else {
      setIsFilterOpen(false)
    }
  }

  const onChangeFundSearch = (event) => {
    setFundSearch(event.currentTarget.value)
  }

  const onChangeFilter = (category) => {
    setSelectedCategory(category)
  }

  const initializeFundsFiltered = () => {
    let _funds = [...funds]
    if (selectedCategory?.length > 0) {
      _funds = _funds.filter((fund) =>
        selectedCategory.includes(fund.categoryName)
      )
    }
    if (selectedFundSearch?.length > 0) {
      _funds = _funds.filter((fund) => {
        // Search
        const fundSearchTest =
          removeSpecialCharacters(fund.fundName)
            .toLowerCase()
            .indexOf(
              removeSpecialCharacters(selectedFundSearch).toLowerCase()
            ) > -1
        return fundSearchTest
      })
    }
    return _funds
  }

  const aspect = getAspect('#separatelyManagedAccounts')
  const _funds = initializeFundsFiltered()

  return (
    <div className="investment-products u-marginBottom5gu">
      <ErrorBoundary
        title="Manage Account Summary"
        message="Manage Account Summary information is currently unavailable due to a technical issue. Please try again later."
        isConstrained={true}
      >
        <div id="manage-account" className="u-paddingVert2gu">
          <div className="u-sizeConstrained Content u-marginBottom5gu">
            <div className="u-posRelative u-sm-paddingVert4gu u-sm-marginBottom4gu">
              <FilterHeader
                title="Filters"
                placeholder="Find a separately managed account…"
                count={selectedCategory.length}
                defaultSearchText={selectedFundSearch}
                isOpen={isFilterOpen}
                onClick={() => onClickToggleFilter(1)}
                onSearch={onChangeFundSearch}
                mode={`product-landing on-key-press ${isMobile ? 'mobile' : ''}`}
                noAutoComplete={true}
              />
              <div
                className={`InvestmentProductsFilterDropDown ${
                  isFilterOpen ? '' : 'u-hidden'
                } `}
              >
                <ManageAccountsFilters
                  categoryOptions={categoryOptions}
                  selectedCategory={selectedCategory}
                  onChange={onChangeFilter}
                  onClose={() => onClickToggleFilter(0)}
                />
              </div>
            </div>
            <div className="u-flex u-flexAlignItemsCenter u-marginTop4gu u-marginBottom2gu">
              <div className="Form-label u-flexAuto">
                Displaying{' '}
                <span className="u-textBold">{_funds.length || 0}</span> fund(s)
              </div>
            </div>
            {isMobile ? (
              <div className="Accordion">
                {_funds.map((fund) => {
                  return (
                    <MobileFundCard
                      asOfDates={grossAsOfDate}
                      key={fund.accountID}
                      fund={fund}
                      product={SEPARATELY_MANAGED_ACCOUNTS_PATH}
                      section={SEPARATELY_MANAGED_ACCOUNTS_PATH}
                      getFixedDecimal={getFixedDecimal}
                    />
                  )
                })}
              </div>
            ) : (
              <div className="InvestmentProductTable">
                <Table
                  columns={aspect.columns}
                  rows={_funds}
                  columnSummaries={aspect.columnSummaries}
                  tableClass="u-sm-block u-overflowX"
                  mode={'no-stripe'}
                  scrollOffset={tableHeaderTop}
                />
              </div>
            )}
          </div>
        </div>
      </ErrorBoundary>
    </div>
  )
}
